import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAfiliados } from '../../reducers/afiliado_slice';
import Table from 'react-bootstrap/Table';
import CustomPrintModal from '../UI/CustomPrintModal1';
import * as XLSX from 'xlsx';
import Select from 'react-select';
import { fetchSituaciones } from '../../reducers/situacion_slice';
import { apellidosNombreFullPersona, formatearFechaSimple } from '../../util/customFormatter';
import { fechaHoraActualBoliviaSoloVista } from '../../util/valueCalculator';

const AfiliadoCumple = () => {
  const dispatch = useDispatch();
  const afiliados = useSelector(state => state.afiliado.afiliados || []);
  const situaciones = useSelector(state => state.situacion.situaciones || []);

  const [selectedSituaciones, setSelectedSituaciones] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const [customTitle, setCustomTitle] = useState('');

  // Obtener fecha actual
  const fechaActual = new Date();
  const diaActual = fechaActual.getDate().toString().padStart(2, '0');
  const mesActual = (fechaActual.getMonth() + 1).toString().padStart(2, '0');
  const fechaActualStr = `${diaActual}-${mesActual}`;

  // Rango de fechas para cumpleaños (formato: DD-MM)
  const [fechaDesde, setFechaDesde] = useState(fechaActualStr);
  const [fechaHasta, setFechaHasta] = useState(fechaActualStr);

  useEffect(() => {
    dispatch(fetchAfiliados());
    dispatch(fetchSituaciones());
  }, [dispatch]);

  useEffect(() => {
    if (situaciones && situaciones.length > 0) {
      const situSelectedDefect = [...situaciones]
        .filter((situ) => situ.alias === "activo")
        .map(situ => ({ value: situ.id, label: situ.nombre }));
      setSelectedSituaciones(situSelectedDefect);
    }
  }, [situaciones]);

  const handleFilterSituacionChange = selected => {
    setSelectedSituaciones(selected || []);
  };

  // Función para verificar si una fecha está en el rango (ignorando el año)
  const isFechaEnRango = (fechaNacimientoStr, desdeStr, hastaStr) => {
    if (!fechaNacimientoStr) return false;

    // Extraer mes y día de la fecha de nacimiento (formato YYYY-MM-DD)
    const partes = fechaNacimientoStr.split('-');
    if (partes.length !== 3) return false;
    
    const mesFecha = parseInt(partes[1], 10);
    const diaFecha = parseInt(partes[2], 10);
    
    // Extraer mes y día del rango (formato DD-MM)
    const desdePartes = desdeStr.split('-');
    const hastaPartes = hastaStr.split('-');
    
    if (desdePartes.length !== 2 || hastaPartes.length !== 2) return false;
    
    const diaDesde = parseInt(desdePartes[0], 10);
    const mesDesde = parseInt(desdePartes[1], 10);
    const diaHasta = parseInt(hastaPartes[0], 10);
    const mesHasta = parseInt(hastaPartes[1], 10);
    
    // Validar que sean números válidos
    if (isNaN(mesFecha) || isNaN(diaFecha) || 
        isNaN(mesDesde) || isNaN(diaDesde) || 
        isNaN(mesHasta) || isNaN(diaHasta)) {
      return false;
    }
    
    // Convertir a número de día del año (1-366)
    const obtenerDiaDelAnio = (mes, dia) => {
      const diasPorMes = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
      let diaDelAnio = 0;
      for (let i = 0; i < mes - 1; i++) {
        diaDelAnio += diasPorMes[i];
      }
      diaDelAnio += dia;
      return diaDelAnio;
    };
    
    const fechaDiaNum = obtenerDiaDelAnio(mesFecha, diaFecha);
    let desdeDiaNum = obtenerDiaDelAnio(mesDesde, diaDesde);
    let hastaDiaNum = obtenerDiaDelAnio(mesHasta, diaHasta);
    
    // Si el rango no cruza fin de año (desde <= hasta)
    if (desdeDiaNum <= hastaDiaNum) {
      return fechaDiaNum >= desdeDiaNum && fechaDiaNum <= hastaDiaNum;
    } else {
      // Si cruza fin de año (ej: 01-11 al 28-02)
      return fechaDiaNum >= desdeDiaNum || fechaDiaNum <= hastaDiaNum;
    }
  };

  // Filtrar afiliados
  const filteredAfiliados = useMemo(() => {
    let result = [...afiliados];

    // Filtrar por situaciones seleccionadas
    if (selectedSituaciones.length > 0) {
      result = result.filter(afiliado => 
        selectedSituaciones.some(situ => situ.value === afiliado.id_situacion)
      );
    }

    // Filtrar por rango de cumpleaños
    result = result.filter(afiliado => {
      const fechaNac = afiliado.persona?.fecha_nac;
      return isFechaEnRango(fechaNac, fechaDesde, fechaHasta);
    });

    return result;
  }, [afiliados, selectedSituaciones, fechaDesde, fechaHasta]);

  // Función para obtener el día del año de una fecha (para ordenar)
  const obtenerDiaDelAnio = (fechaStr) => {
    if (!fechaStr) return 0;
    const partes = fechaStr.split('-');
    if (partes.length !== 3) return 0;
    const mes = parseInt(partes[1], 10);
    const dia = parseInt(partes[2], 10);
    
    const diasPorMes = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    let diaDelAnio = 0;
    for (let i = 0; i < mes - 1; i++) {
      diaDelAnio += diasPorMes[i];
    }
    diaDelAnio += dia;
    return diaDelAnio;
  };

  // Ordenar afiliados
  const sortedAfiliados = useMemo(() => {
    const sorted = [...filteredAfiliados];
    sorted.sort((a, b) => {
      let aValue, bValue;
      
      if (sortConfig.key === 'num_reg') {
        aValue = parseInt(a.num_reg, 10);
        bValue = parseInt(b.num_reg, 10);
      } else if (sortConfig.key === 'fecha_nac') {
        // Ordenar por día del año (ignorando el año)
        aValue = obtenerDiaDelAnio(a.persona?.fecha_nac);
        bValue = obtenerDiaDelAnio(b.persona?.fecha_nac);
      } else {
        aValue = `${apellidosNombreFullPersona(a.persona)}`.toLowerCase();
        bValue = `${apellidosNombreFullPersona(b.persona)}`.toLowerCase();
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredAfiliados, sortConfig]);

  const handleSort = key => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Formatear fecha para mostrar solo día/mes
  const formatearDiaMes = (fechaStr) => {
    if (!fechaStr) return '';
    const partes = fechaStr.split('-');
    if (partes.length !== 3) return '';
    return `${partes[2]}/${partes[1]}`;
  };

  // EXPORTACIÓN A EXCEL
  const handleExport = () => {
    const exportData = sortedAfiliados.map((afiliado, index) => {
      const nombreCompleto = apellidosNombreFullPersona(afiliado.persona);
      
      return {
        '#': index + 1,
        'Nombre Completo': nombreCompleto,
        'Número Registro': afiliado.num_reg,
        'C.I.': afiliado.persona?.ci || '',
        'Fecha Registro C.A.B.': formatearFechaSimple(afiliado.fecha_reg_cab),
        'Fecha Nacimiento': formatearFechaSimple(afiliado.persona?.fecha_nac),
        'Día/Mes Nacimiento': formatearDiaMes(afiliado.persona?.fecha_nac),
        'Residencia': afiliado.lugar_residencia?.nombre || '',
        'Situación': afiliado.situacion?.nombre || 'N/A',
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Cumpleañeros');
    XLSX.writeFile(workbook, `cumpleaneros_${fechaDesde}_a_${fechaHasta}.xlsx`);
  };

  // PREVIEW PARA PDF
  const getPreviewContent = () => {
    const headRow = ['#', 'Nombre Completo', 'Número Registro', 'C.I.', 'Fecha Registro C.A.B.', 'Fecha Nacimiento', 'Residencia', 'Situación'];

    const bodyContent = sortedAfiliados.map((afiliado, index) => {
      return [
        index + 1,
        apellidosNombreFullPersona(afiliado.persona),
        afiliado.num_reg,
        afiliado.persona?.ci || '',
        formatearFechaSimple(afiliado.fecha_reg_cab),
        formatearFechaSimple(afiliado.persona?.fecha_nac),
        afiliado.lugar_residencia?.nombre || '',
        afiliado.situacion?.nombre || 'N/A',
      ];
    });

    return {
      head: [headRow],
      body: bodyContent,
      title2: customTitle || 'LISTA DE CUMPLEAÑEROS',
      title3: `Rango: ${fechaDesde} al ${fechaHasta}`,
    };
  };

  const situacionesSelect = situaciones
    .filter(s => s.alias && !s.alias.includes("preinscrito"))
    .map(situ => ({ value: situ.id, label: situ.nombre }));

  return (
    <>
      <div className="list-container list1">
        <h1 className="table-title">REPORTE DE CUMPLEAÑEROS</h1>
        <div className="list-content">
          <div className='row mb-3'>
            <div className="col-md-3">
              <label className="form-label">Rango de fechas (DD-MM)</label>
              <div className="d-flex gap-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Desde (DD-MM)"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Hasta (DD-MM)"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                />
              </div>
              <small className="text-muted">Ejemplo: 15-03 (15 de marzo)</small>
            </div>

            <div className='col-md-4'>
              <label className="form-label">Situación</label>
              <Select
                isMulti
                options={situacionesSelect}
                value={selectedSituaciones}
                onChange={handleFilterSituacionChange}
                placeholder="Filtrar por situación..."
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Título del reporte</label>
              <input
                type="text"
                className="form-control"
                placeholder="Título personalizado"
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
              />
            </div>
          </div>

          <div className="list-container-header d-flex justify-content-end mb-3">
            <button
              title="Imprimir"
              onClick={() => setShowPreviewModal(true)}
              className="btn btn-sm btn-primary me-2"
            >
              <i className="bi bi-printer"></i> Imprimir
            </button>

            <button
              title="Exportar a Excel"
              onDoubleClick={handleExport}
              className="btn btn-sm btn-success"
            >
              <i className="bi bi-file-spreadsheet"></i> Exportar
            </button>
          </div>

          {sortedAfiliados.length === 0 ? (
            <p>No hay cumpleañeros en el rango seleccionado.</p>
          ) : (
            <div className="table-container">
              <Table bordered hover size="sm">
                <thead style={{ backgroundColor: '#b2aea3' }}>
                  <tr>
                    <th>#</th>
                    <th onClick={() => handleSort('nombre_completo')} style={{ cursor: 'pointer' }}>
                      Nombre Completo {sortConfig.key === 'nombre_completo' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('num_reg')} style={{ cursor: 'pointer' }}>
                      Número Registro {sortConfig.key === 'num_reg' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>C.I.</th>
                    <th>Fecha Registro C.A.B.</th>
                    <th onClick={() => handleSort('fecha_nac')} style={{ cursor: 'pointer' }}>
                      Fecha Nacimiento {sortConfig.key === 'fecha_nac' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Residencia</th>
                    <th>Situación</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAfiliados.map((afiliado, index) => (
                    <tr key={afiliado.id}>
                      <td>{index + 1}</td>
                      <td>{apellidosNombreFullPersona(afiliado.persona)}</td>
                      <td>{afiliado.num_reg}</td>
                      <td>{afiliado.persona?.ci}</td>
                      <td>{formatearFechaSimple(afiliado.fecha_reg_cab)}</td>
                      <td>{formatearFechaSimple(afiliado.persona?.fecha_nac)}</td>
                      <td>{afiliado.lugar_residencia?.nombre}</td>
                      <td>{afiliado.situacion?.nombre || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          <CustomPrintModal
            show={showPreviewModal}
            handleClose={() => setShowPreviewModal(false)}
            title1={`LISTA DE CUMPLEAÑEROS${customTitle && `\n${customTitle}`}`}
            title2={`Rango: ${fechaDesde} al ${fechaHasta}${selectedSituaciones.length === 0 ? "" : `\nSituación: ${selectedSituaciones.map(s => s.label).join(", ")}`}`}
            title3={`Fecha de consulta: ${fechaHoraActualBoliviaSoloVista("soloFecha")} / Resultado: ${sortedAfiliados.length} registros`}
            content={getPreviewContent()}
          />
        </div>
      </div>
    </>
  );
};

export default AfiliadoCumple;